import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { BookingCodeRule } from "../api/contracts/booking-code";

/**
 * sessionStorage key, scoped to the site the page is showing.
 *
 * Every WBE site is served from one origin with the site in the first path
 * segment, so an unscoped key is shared by every site visited in the same tab —
 * and an applied code rode along into the next one. Where the two sites belong to
 * different facilities the code resolved to nothing but still showed its pill and
 * the previous site's rules; where they belong to the SAME facility and the
 * agreement is enabled on both, it silently priced a booking the guest never
 * entered it for.
 *
 * Read once at module load, which is how the app already treats the identifier
 * (`ConfigurationProvider` reads it the same way and never re-reads): there is no
 * UI that moves between sites without a page load. Scoping rather than clearing
 * means going back to the first site restores the code that belongs there.
 */
export const bookingCodeStorageKey = (
  pathname: string = window.location.pathname,
): string => `booking-code-storage:${pathname.split("/")[1] ?? ""}`;

interface BookingCodeState {
  /** The raw code the user entered / deep-linked. */
  code: string | null;
  /** Whether the code resolved to an active, WBE-available agreement. */
  applied: boolean;
  /** Agreement rules returned by the resolve endpoint (for CXA-387 display). */
  rules: BookingCodeRule[];
  /**
   * What is currently typed into the code input, applied or not. Lives here
   * rather than in each input's local state because the desktop segment and the
   * mobile sheet are separate components that never coexist: crossing the
   * breakpoint unmounts one and mounts the other, which used to drop an
   * un-applied draft on the floor. One field, two renderers.
   *
   * Deliberately NOT persisted — a reload keeps the applied code (below) and
   * forgets stray typing, as before.
   */
  draft: string;
  setDraft: (draft: string) => void;
  /**
   * True once this code has been seen pricing something with a shared room cap
   * ("Begränsat antal"). The cap is consumed across bookings and is NOT checked
   * on a catalog search, so a card can advertise a discount the cart then can't
   * honour. Remembering that the guest was shown a capped offer is what lets the
   * cart say "it ran out" instead of quietly charging BAR — and gating on it is
   * what stops a mistyped code from producing the same message.
   *
   * Persisted with the code, so a reload doesn't lose the explanation. Reset by
   * applyCode/clearCode: a different code has its own caps.
   */
  limitedOfferSeen: boolean;
  markLimitedOfferSeen: () => void;
  /**
   * True once the guest has been told this code's capped offer ran out.
   *
   * Lives here rather than in the hook because the cart and the checkout mount
   * that hook separately — a per-component guard announced the same loss again
   * the moment the guest walked from one page to the other. Not persisted: the
   * toast is a moment, and the summary's standing note is what survives a reload.
   */
  limitedOfferAnnounced: boolean;
  markLimitedOfferAnnounced: () => void;
  /** Apply a resolved code. */
  applyCode: (input: { code: string; rules?: BookingCodeRule[] }) => void;
  /** Clear the applied code (remove ✕). */
  clearCode: () => void;
  hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
}

export const useBookingCodeStore = create<BookingCodeState>()(
  persist(
    (set) => ({
      code: null,
      applied: false,
      rules: [],
      draft: "",
      limitedOfferSeen: false,
      limitedOfferAnnounced: false,
      setDraft: (draft) => set({ draft }),
      markLimitedOfferSeen: () => set({ limitedOfferSeen: true }),
      markLimitedOfferAnnounced: () => set({ limitedOfferAnnounced: true }),
      // Applying and clearing both settle the draft on the new truth, so the
      // input never disagrees with the pill next to it.
      applyCode: ({ code, rules }) =>
        set({
          code,
          applied: true,
          rules: rules ?? [],
          draft: code,
          limitedOfferSeen: false,
          limitedOfferAnnounced: false,
        }),
      clearCode: () => {
        set({
          code: null,
          applied: false,
          rules: [],
          draft: "",
          limitedOfferSeen: false,
          limitedOfferAnnounced: false,
        });
        useBookingCodeStore.persist.clearStorage();
      },
      hasHydrated: false,
      setHasHydrated: (state: boolean) => set({ hasHydrated: state }),
    }),
    {
      name: bookingCodeStorageKey(),
      storage: createJSONStorage(() => sessionStorage),
      // Persist only the data, not the draft, the hydration flag or the action
      // fns.
      partialize: (state) => ({
        code: state.code,
        applied: state.applied,
        rules: state.rules,
        limitedOfferSeen: state.limitedOfferSeen,
      }),
      // The draft isn't persisted, so seed it from the restored code — otherwise
      // a reload would show the pill with a code and the input empty.
      onRehydrateStorage: () => (state) => {
        if (state?.applied && state.code) state.setDraft(state.code);
      },
    },
  ),
);
